/**
 * PRD.330 Phase 2 — Pure presentation logic for the shared report UI.
 *
 * Derives what the UI shows from (definition, applied query, supplied
 * result). Counts, aggregates and facet options are always taken from the
 * supplied result — never recomputed from the rows on the current page.
 */
import {
  buildCategoryBarCategories,
  buildReportTimeBuckets,
  deriveReportAggregates,
  fillReportTimeSeries,
  selectReportTimeBucket,
  effectiveSort,
  hasActiveFilters,
  reportPageCount,
  type ReportDefinition,
  type ReportFacetOption,
  type ReportGroupedRow,
  type ReportQuery,
  type ReportSortDirection,
  type ReportVisualizationResult,
} from '@helios/domain';

// ─── View status ───

export type ReportViewStatus = 'loading' | 'error' | 'empty' | 'no-results' | 'ready';

/**
 * loading    — the current query has not resolved yet;
 * error      — the query failed;
 * empty      — resolved, zero rows, no search/column filters (nothing in range);
 * no-results — resolved, zero rows because of search/column filters;
 * ready      — resolved with matches.
 */
export function deriveReportViewStatus<C extends string>(input: {
  query: ReportQuery<C>;
  loading: boolean;
  error: boolean;
  totalCount: number;
}): ReportViewStatus {
  if (input.error) return 'error';
  if (input.loading) return 'loading';
  if (input.totalCount > 0) return 'ready';
  return hasActiveFilters(input.query) ? 'no-results' : 'empty';
}

// ─── Column header state ───

export interface ReportHeaderState {
  sortable: boolean;
  /** Direction when this column is the effective sort, else null. */
  sortDirection: ReportSortDirection | null;
  /** True when the sort shown is the definition default (not user-chosen). */
  sortIsDefault: boolean;
  ariaSort: 'ascending' | 'descending' | 'none';
  filterable: boolean;
  filterActive: boolean;
  /** Accessible names for the header controls (state is never colour-only). */
  sortButtonLabel: string;
  filterButtonLabel: string;
}

export function columnHeaderState<C extends string>(
  definition: ReportDefinition<C>,
  query: ReportQuery<C>,
  columnId: C,
): ReportHeaderState {
  const column = definition.columns.find((c) => c.id === columnId);
  if (!column) throw new Error(`Unknown report column: ${columnId}`);
  const sort = effectiveSort(definition, query);
  const sortDirection = column.sortable && sort.columnId === columnId ? sort.direction : null;
  const filterActive = column.filter !== null && query.filters[columnId] !== undefined;
  const sortText =
    sortDirection === 'asc'
      ? 'sorted ascending'
      : sortDirection === 'desc'
        ? 'sorted descending'
        : 'not sorted';
  return {
    sortable: column.sortable,
    sortDirection,
    sortIsDefault: sortDirection !== null && query.sort === null,
    ariaSort:
      sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none',
    filterable: column.filter !== null,
    filterActive,
    sortButtonLabel: `Sort by ${column.label}, currently ${sortText}`,
    filterButtonLabel: `Filter ${column.label}${filterActive ? ', filter active' : ''}`,
  };
}

// ─── Facet options ───

export interface ReportFacetListItem extends ReportFacetOption {
  /** Applied/selected value that the supplied facet data no longer contains. */
  stale: boolean;
}

/**
 * The option list a facet popover shows: the SUPPLIED facet options (from
 * the query layer) plus any selected value missing from them, kept visible
 * with count 0 and marked stale so it is never silently retained or dropped.
 */
export function buildFacetList(
  supplied: ReadonlyArray<ReportFacetOption>,
  selected: ReadonlyArray<string>,
  labelFor: (value: string) => string,
): ReportFacetListItem[] {
  const known = new Set(supplied.map((o) => o.value));
  const items: ReportFacetListItem[] = supplied.map((o) => ({ ...o, stale: false }));
  for (const value of selected) {
    if (!known.has(value)) {
      items.push({ value, label: labelFor(value), count: 0, stale: true });
      known.add(value);
    }
  }
  return items.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

// ─── Summary ───

export interface ReportSummaryItem {
  key: string;
  label: string;
  /** Raw aggregate value from the result (sums in minor units), or null while unknown. */
  value: number | null;
}

/**
 * One summary item per requested non-count aggregate (the count is shown
 * separately as "N <countLabel>"), read from the supplied result.
 * `labels` lets the consumer name each aggregate (e.g. "Total Collection
 * Amount"); the shell never hard-codes report-specific wording.
 */
export function buildSummaryItems<C extends string>(
  definition: ReportDefinition<C>,
  aggregates: Readonly<Record<string, number>> | null,
  labels: Readonly<Record<string, string>>,
): ReportSummaryItem[] {
  return definition.aggregates
    .filter((agg) => agg.kind !== 'count')
    .map((agg) => ({
      key: agg.key,
      label: labels[agg.key] ?? agg.key,
      value: aggregates && typeof aggregates[agg.key] === 'number' ? aggregates[agg.key]! : null,
    }));
}

// ─── Pagination ───

export interface ReportPageInfo {
  page: number;
  pageCount: number;
  /** 1-based index of the first/last row on this page; 0 when there are no rows. */
  firstRow: number;
  lastRow: number;
  totalCount: number;
  canPrev: boolean;
  canNext: boolean;
}

export function reportPageInfo(page: number, pageSize: number, totalCount: number): ReportPageInfo {
  const pageCount = reportPageCount(totalCount, pageSize);
  const firstRow = totalCount === 0 ? 0 : Math.min((page - 1) * pageSize + 1, totalCount);
  const lastRow = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);
  return {
    page,
    pageCount,
    firstRow,
    lastRow,
    totalCount,
    canPrev: page > 1,
    canNext: page < pageCount,
  };
}

// ─── Result assembly (executor output → Phase 1 ReportResult) ───

export interface ReportQueryPart<T> {
  data: T[];
  loaded: boolean;
  error: Error | null;
}

/**
 * Combine the executor's rows / summary / facet query parts into a Phase 1
 * `ReportResult`. Count and aggregates come ONLY from the summary part (the
 * full filtered result), facets ONLY from the facet parts — rows are passed
 * through untouched, in the order the query layer returned them.
 * Error details are kept out of `error` so raw internals never reach users.
 */
export function assembleReportResult<Row, C extends string>(input: {
  definition: ReportDefinition<C>;
  rows: ReportQueryPart<Row>;
  summary: ReportQueryPart<Record<string, unknown>>;
  facets: ReadonlyArray<{ columnId: C; part: ReportQueryPart<{ value: unknown; count: unknown }> }>;
  labelFor: (columnId: C, value: string) => string;
  compileFailed?: boolean;
  /** PRD.330 Phase 4 — the applied query (for visualization dimension filters). */
  query?: ReportQuery<C>;
  /** One grouped-projection part per `definition.visualizations` entry. */
  visualizations?: ReadonlyArray<{ id: string; part: ReportQueryPart<Record<string, unknown>> }>;
}): {
  status: 'loading' | 'ready' | 'error';
  rows: Row[];
  totalCount: number;
  aggregates: Record<string, number>;
  facets: Partial<Record<C, ReportFacetOption[]>>;
  /** Per facet column: options not yet loaded for the current query. */
  facetLoading: Partial<Record<C, boolean>>;
  /** Independent per-visualization results: a chart failure never fails the table. */
  visualizations: Record<string, ReportVisualizationResult>;
  error?: string;
} {
  const parts = [input.rows, input.summary, ...input.facets.map((f) => f.part)];
  const failed = input.compileFailed === true || parts.some((p) => p.error !== null);
  const loaded = parts.every((p) => p.loaded);

  const summaryRow = input.summary.data[0] ?? {};
  const sqlAggregates: Record<string, number> = {};
  for (const agg of input.definition.aggregates) {
    if (agg.kind === 'ratio') continue;
    const raw = Number(summaryRow[agg.key]);
    sqlAggregates[agg.key] = Number.isFinite(raw) ? raw : 0;
  }
  // Ratio aggregates (e.g. Average Payment) are derived from the FULL-query
  // summary values — never from page rows.
  const aggregates = deriveReportAggregates(input.definition, sqlAggregates);
  const countKey = input.definition.aggregates.find((a) => a.kind === 'count')?.key;
  const totalCount = countKey ? aggregates[countKey]! : 0;

  const facets: Partial<Record<C, ReportFacetOption[]>> = {};
  const facetLoading: Partial<Record<C, boolean>> = {};
  for (const { columnId, part } of input.facets) {
    facetLoading[columnId] = !part.loaded && part.error === null;
    facets[columnId] = part.data
      .filter((r) => r.value !== null && r.value !== undefined)
      .map((r) => {
        const value = String(r.value);
        return { value, label: input.labelFor(columnId, value), count: Number(r.count) || 0 };
      });
  }

  // Visualizations come ONLY from their own grouped queries (never from rows)
  // and are deliberately kept out of the table's overall status above.
  const visualizations: Record<string, ReportVisualizationResult> = {};
  for (const { id, part } of input.visualizations ?? []) {
    const viz = input.definition.visualizations?.find((v) => v.id === id);
    if (!viz) continue;
    const status: ReportVisualizationResult['status'] =
      input.compileFailed === true || part.error !== null
        ? 'error'
        : part.loaded
          ? 'ready'
          : 'loading';
    const measure = (r: Record<string, unknown>) => ({
      primary: Number(r[viz.measure.key]) || 0,
      ...(viz.secondaryMeasure ? { secondary: Number(r[viz.secondaryMeasure.key]) || 0 } : {}),
    });

    if (viz.kind === 'timeSeries') {
      // PRD.330 Phase 5 — bucket derived from the applied range only; every
      // expected calendar bucket present (zero-filled).
      const range = input.query?.dateRange;
      const bucket = range ? selectReportTimeBucket(range) : null;
      const expected = range && bucket ? buildReportTimeBuckets(range, bucket) : [];
      visualizations[id] = {
        kind: 'timeSeries',
        // One-day range → explicitly not applicable (never "empty"/zero-filled).
        status: bucket === null ? 'notApplicable' : status,
        bucket,
        points: fillReportTimeSeries(
          expected,
          part.data.map((r) => ({
            bucket: r.bucket === null || r.bucket === undefined ? null : String(r.bucket),
            ...measure(r),
          })),
          viz.secondaryMeasure !== undefined,
        ),
      };
      continue;
    }

    const grouped: ReportGroupedRow[] = part.data.map((r) => ({
      category: r.category === null || r.category === undefined ? null : String(r.category),
      ...measure(r),
    }));
    visualizations[id] = {
      kind: 'categoryBar',
      status,
      categories: buildCategoryBarCategories({
        visualization: viz,
        rows: grouped,
        dimensionFilter: input.query?.filters[viz.dimension],
        labelFor: (value) => input.labelFor(viz.dimension, value),
      }),
    };
  }

  return {
    status: failed ? 'error' : loaded ? 'ready' : 'loading',
    rows: input.rows.data,
    totalCount,
    aggregates,
    facets,
    facetLoading,
    visualizations,
    ...(failed ? { error: "Couldn't load this report." } : {}),
  };
}

/**
 * PRD.330 Phase 4 — bar length as a 0..1 fraction of the largest category.
 * Takes RAW numbers (cents), never formatted money strings.
 */
export function barFraction(value: number, max: number): number {
  if (typeof value !== 'number' || typeof max !== 'number') return 0;
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0 || value <= 0) return 0;
  return Math.min(1, value / max);
}

// ─── Time-series labels (PRD.330 Phase 5) ───

const MONTH_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

function localDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

/** Visible grouping indicator, e.g. "Grouped by week". */
export function timeSeriesGroupingLabel(bucket: 'day' | 'week' | 'month'): string {
  return `Grouped by ${bucket}`;
}

/** Panel subtitle: the grouping, or "N/A" for a single-day range (bucket null). */
export function timeSeriesSubtitle(bucket: 'day' | 'week' | 'month' | null): string {
  return bucket === null ? 'N/A' : timeSeriesGroupingLabel(bucket);
}

/** Daily series longer than this take a full-width row (e.g. This month / Last month). */
export const TIME_SERIES_HALF_WIDTH_MAX_DAYS = 7;

/**
 * Whether the time-series card should own a full-width row in the report's
 * visualization area. Derived from the applied range's bucket and bucket count
 * (never from preset identity):
 *   - N/A (one-day range) → compact full-width status row;
 *   - daily grouping over more than 7 days → full width, so ~30 daily columns
 *     aren't squeezed into half the row;
 *   - otherwise (≤ 7 days, weekly, monthly) → shares the row side by side.
 */
export function timeSeriesSpansFullRow(result: {
  status: string;
  bucket: 'day' | 'week' | 'month' | null;
  points: ReadonlyArray<unknown>;
}): boolean {
  if (result.status === 'notApplicable' || result.bucket === null) return true;
  return result.bucket === 'day' && result.points.length > TIME_SERIES_HALF_WIDTH_MAX_DAYS;
}

/**
 * Display label for one bucket (display only — never fed back into queries).
 * day "Sep 24"; week "Sep 21–27" / "Sep 28–Oct 4" (the dates actually
 * covered inside the range); month "Sep 2026". `withYear` appends the year
 * to day/week labels when a series spans more than one year.
 */
export function timeSeriesPointLabel(
  point: { key: string; from: string; to: string },
  bucket: 'day' | 'week' | 'month',
  withYear = false,
): string {
  const year = withYear ? `, ${point.from.slice(0, 4)}` : '';
  if (bucket === 'month') {
    return localDate(`${point.key}-01`).toLocaleDateString('en-PH', {
      month: 'short',
      year: 'numeric',
    });
  }
  const from = localDate(point.from);
  if (bucket === 'day' || point.from === point.to) {
    return `${from.toLocaleDateString('en-PH', MONTH_DAY)}${year}`;
  }
  const to = localDate(point.to);
  const toText =
    from.getMonth() === to.getMonth()
      ? String(to.getDate())
      : to.toLocaleDateString('en-PH', MONTH_DAY);
  return `${from.toLocaleDateString('en-PH', MONTH_DAY)}–${toText}${year}`;
}
